import { useEffect, useState } from "react";
import { Crown, TrendingUp, Users, Banknote, Loader2 } from "lucide-react";
import { fetchAdminProRevenue } from "@/lib/api";

/**
 * Admin widget — live Fodindin Pro financial KPIs (MRR, ARR, active subs).
 * Auto-refresh every 60s.
 */
export default function AdminProRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => fetchAdminProRevenue().then(setData).catch(() => null).finally(() => setLoading(false));
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <section className="glass-card p-5 lg:col-span-3" data-testid="admin-pro-revenue">
        <Loader2 className="animate-spin text-amber-500" size={20} />
      </section>
    );
  }
  if (!data) return null;

  const f = (n) => (n || 0).toLocaleString("fr-FR");

  return (
    <section
      className="glass-card p-5 lg:col-span-3 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200"
      data-testid="admin-pro-revenue"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-bold text-lg inline-flex items-center gap-2 text-amber-900">
          <Crown size={18} className="text-amber-600" /> Revenus Fodindin Pro
        </h2>
        <span className="text-[11px] text-amber-700">
          Mise à jour auto · {new Date(data.as_of).toLocaleTimeString("fr-FR")}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl p-4 bg-white/70 border-2 border-amber-200" data-testid="metric-active-subs">
          <Users className="text-amber-600 mb-2" size={18} />
          <div className="text-3xl font-bold text-amber-900">{f(data.active_subscribers)}</div>
          <div className="text-xs text-amber-800/80">Abonnés actifs</div>
        </div>
        <div className="rounded-2xl p-4 bg-white/70 border-2 border-emerald-200" data-testid="metric-mrr">
          <TrendingUp className="text-emerald-600 mb-2" size={18} />
          <div className="text-3xl font-bold text-emerald-900">{f(data.mrr_fcfa)}</div>
          <div className="text-xs text-emerald-800/80">MRR (FCFA / mois)</div>
        </div>
        <div className="rounded-2xl p-4 bg-white/70 border-2 border-orange-200" data-testid="metric-arr">
          <Banknote className="text-orange-600 mb-2" size={18} />
          <div className="text-3xl font-bold text-orange-900">{f(data.arr_fcfa)}</div>
          <div className="text-xs text-orange-800/80">ARR projeté (FCFA)</div>
        </div>
        <div className="rounded-2xl p-4 bg-white/70 border-2 border-red-200" data-testid="metric-churn">
          <div className="text-3xl font-bold text-red-700">{f(data.churned_30d)}</div>
          <div className="text-xs text-red-800/80">Churn 30 derniers j</div>
          <div className="text-[10px] text-[var(--ink-soft)] mt-1">
            ARPU : {f(data.arpu_fcfa)} FCFA
          </div>
        </div>
      </div>

      <div className="text-[11px] text-amber-700 mt-3">
        Cumul transactions payées : <b>{f(data.total_paid_transactions)}</b> · Revenu brut historique :
        <b className="ml-1">{f(data.gross_revenue_fcfa)} FCFA</b>
      </div>
    </section>
  );
}
